

import { FocusMonitor } from '@angular/cdk/a11y';
import { Direction, Directionality } from '@angular/cdk/bidi';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BooleanInput, OnChangeType, OnTouchedType } from 'mwc/core/types';
import { InputBoolean } from 'mwc/core/util';

export interface MwcCheckBoxOptionInterface {
  label: string;
  value: string;
  checked?: boolean;
  disabled?: boolean;
}

@Component({
  selector: 'mwc-checkbox-group',
  exportAs: 'mwcCheckboxGroup',
  preserveWhitespaces: false,
  encapsulation: ViewEncapsulation.None,
  template: `
    <label
      mwc-checkbox
      class="mwc-checkbox-group-item"
      *ngFor="let o of options; trackBy: trackByOption"
      [mwcDisabled]="o.disabled || mwcDisabled"
      [mwcChecked]="o.checked!"
      (mwcCheckedChange)="onCheckedChange(o, $event)"
    >
      <span>{{ o.label }}</span>
    </label>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MwcCheckboxGroupComponent),
      multi: true
    }
  ],
  host: {
    class: 'mwc-checkbox-group',
    '[class.mwc-checkbox-group-rtl]': `dir === 'rtl'`
  }
})
export class MwcCheckboxGroupComponent implements ControlValueAccessor, OnInit, OnDestroy {
  static ngAcceptInputType_mwcDisabled: BooleanInput;

  onChange: OnChangeType = () => {};
  onTouched: OnTouchedType = () => {};
  options: MwcCheckBoxOptionInterface[] = [];
  @Input() @InputBoolean() mwcDisabled = false;

  dir: Direction = 'ltr';

  private destroy$ = new Subject<void>();
  private isMwcDisableFirstChange: boolean = true;

  trackByOption(_: number, option: MwcCheckBoxOptionInterface): string {
    return option.value;
  }

  onCheckedChange(option: MwcCheckBoxOptionInterface, checked: boolean): void {
    option.checked = checked;
    this.onChange(this.options);
  }

  constructor(
    private elementRef: ElementRef,
    private focusMonitor: FocusMonitor,
    private cdr: ChangeDetectorRef,
    @Optional() private directionality: Directionality
  ) {}

  ngOnInit(): void {
    this.focusMonitor
      .monitor(this.elementRef, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(focusOrigin => {
        if (!focusOrigin) {
          Promise.resolve().then(() => this.onTouched());
        }
      });

    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
      this.cdr.detectChanges();
    });

    this.dir = this.directionality.value;
  }

  ngOnDestroy(): void {
    this.focusMonitor.stopMonitoring(this.elementRef);
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: MwcCheckBoxOptionInterface[]): void {
    this.options = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: OnChangeType): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchedType): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.mwcDisabled = (this.isMwcDisableFirstChange && this.mwcDisabled) || disabled;
    this.isMwcDisableFirstChange = false;
    this.cdr.markForCheck();
  }
}
